import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { FloppyDiskIcon } from '@phosphor-icons/react/dist/csr/FloppyDisk';
import { XIcon } from '@phosphor-icons/react/dist/csr/X';

import { RoleCheckboxes } from './RoleCheckboxes';
import { TemporaryPassword } from './TemporaryPassword';
import { Button } from '../../design-system/primitives/Button/Button';
import { Dialog } from '../../design-system/composites/Dialog/Dialog';
import { Input } from '../../design-system/primitives/Input/Input';
import { Select } from '../../design-system/primitives/Select/Select';
import {
  ApiError,
  NETWORK_ERROR_CODE,
  PAGE_EXPIRED_CODE,
  TOO_MANY_REQUESTS_CODE,
} from '../../lib/api/client';
import { dispensariesApi, type Dispensary } from '../../lib/api/reference';
import { usersApi, type AdminUser, type Role, type UserInput } from '../../lib/api/users';
import { referenceName } from '../../lib/format/referenceName';
import styles from './UserFormDialog.module.css';

/**
 * The create/edit form, and the second call that assigns roles.
 *
 * ONE COMPONENT, TWO MODES. `user` absent is create (`POST /users`); `user`
 * present is edit (`PATCH /users/{uuid}`). The two share every field, the
 * in-flight guard, the validation-error mapping and the role picker, so one
 * component earns its keep — a "CreateUserDialog" and an "EditUserDialog"
 * copy-pasted from each other is two places for the next field to drift.
 *
 * ## Edits resend only what changed
 *
 * `UpdateUserRequest` marks every field `sometimes`: omitting a key leaves it
 * unchanged server-side. `diffInput` compares the CONVERTED payload shape
 * (`toInput`) against the same shape computed from the values the dialog
 * opened with, not the raw text state — an untouched `dispensary_id` field
 * holds the string `"12"` the whole time, and `toInput` is what turns both
 * sides into the number `12` so the comparison is not a false positive on
 * every single edit.
 *
 * ## Role assignment is a second call, and it is not symmetric
 *
 * `POST /users` does not accept roles, so a brand-new account calls
 * `PUT /users/{uuid}/roles` only when something was actually ticked — an
 * empty PUT right after creation would be a redundant round trip against an
 * account that already holds nothing. Editing is different: unticking every
 * role IS the edit, and the trigger here is "the selection changed since the
 * dialog opened" (`rolesChanged`) rather than "the selection is non-empty" —
 * that is what lets an edit that removes every role still send `[]`, while an
 * edit that never touched the picker sends nothing at all.
 *
 * ## The second call can fail without undoing the first
 *
 * If `assignRoles` rejects after a successful create or update, the account
 * still exists — `generalFailure` says so rather than leaving the
 * administrator to guess. Create and edit react differently, and both were
 * measured, not assumed:
 *
 * - **Create** shows `TemporaryPassword` regardless of the roles outcome —
 *   losing it because a SECOND, unrelated call failed would strand the one
 *   thing that cannot be regenerated by rereading the screen. `onSaved` is
 *   deferred to acknowledgement either way, so the failure banner and the
 *   password are both on screen together if the second call failed.
 * - **Edit** does NOT call `onSaved` when the roles call failed. `onSaved`
 *   is what `UserListScreen` uses to close this dialog (`handleSaved` calls
 *   `setFormOpen(false)`), and React 18 batches a synchronous `onSaved` +
 *   the parent's resulting re-render into one commit — a version that called
 *   `onSaved(updated)` unconditionally closed the dialog in the SAME frame
 *   `setGeneralFailure` painted the message into, so the message never
 *   reached the screen and the administrator watched the dialog close as if
 *   the edit had fully succeeded. Holding `onSaved` back until the roles call
 *   is known to have succeeded (or was never attempted) is what keeps the
 *   dialog open long enough for `generalFailure` to actually render.
 *
 * ## Which response the row is merged from
 *
 * ONE RULE, BOTH PATHS: the row is merged from whichever response LAST
 * reflects the server's state — the roles-call response
 * (`usersApi.assignRoles`'s return) whenever that call ran and succeeded,
 * and the CREATE/UPDATE response otherwise. Neither `POST /users` nor
 * `PATCH /users/{uuid}` can know about a role change made in the SAME
 * submit: each is awaited and answers BEFORE the roles PUT is even sent,
 * and neither accepts a `roles` key at all. Merging either one
 * unconditionally is the same defect twice over — a row that silently
 * shows no roles (create) or its PRE-edit roles (edit) until something else
 * refetches the list.
 *
 * - **Edit**: `rowAfterSave` starts as the PATCH response `updated` and is
 *   overwritten by `assignRoles`'s return inside the `try` that calls it —
 *   see the earlier section for why a FAILED roles call must not reach
 *   `onSaved` at all here.
 * - **Create**: `createdUser` is set to the `POST /users` response
 *   IMMEDIATELY — the temporary-password panel has to render, and the
 *   password has to be readable, the instant creation succeeds, regardless
 *   of whether a role was even ticked (see the section above: that
 *   guarantee must survive). If `assignRoles` then runs and succeeds,
 *   `createdUser` is set AGAIN to its response. `onSaved`, called later
 *   from `acknowledgeTemporaryPassword` on Dismiss, reads whichever value
 *   `createdUser` holds AT THAT TIME — by then both async calls have long
 *   settled, so it is the roles-merged row when the assignment succeeded,
 *   and the plain created row otherwise. The temporary password is never
 *   touched by any of this, on either path.
 *
 * Caught by task 13c's browser walk, not by any unit test: jsdom never
 * disagreed with itself on this, because nothing there re-reads the merged
 * row through a second, independent render pass the way opening the edit
 * dialog again does. The unit tests below (`UserFormDialog.test.tsx`) pin
 * the fix by asserting `onSaved` receives the object a SEPARATE,
 * distinguishable mock of `assignRoles` returned — not merely a shape
 * that happens to agree with the create/update mock by coincidence.
 *
 * ## Why the required check happens here, not only on the server
 *
 * Not for the server's sake — the server validates every field regardless.
 * `missing` exists so a Save on an all-blank required field does not spend a
 * network round trip (and the 422 it would draw) to say what the client
 * already knows, mirroring `LoginPage.tsx`'s identical `missing` set.
 *
 * ## The dispensary is a picker, not a number
 *
 * It used to be a free-text box for the dispensary's INTEGER primary key,
 * with a digits-only guard in front of it because `Number("1e3")` is `1000`
 * and `Number("0x10")` is `16`, so a typo could silently clear an existing
 * association rather than draw an error. That guard is gone with the box:
 * the value now comes from `GET /reference/dispensaries` and is a uuid the
 * server already knows, or it is empty.
 *
 * The list is loaded WHEN THE DIALOG OPENS, not when the module does, and a
 * failure to load is not fatal — the field renders with only "no dispensary"
 * in it and the rest of the form still saves. Blocking an administrator from
 * correcting an email because a reference list did not load would be the
 * wrong trade; the picker holding a value it cannot name would be a worse
 * one, and cannot happen because the value IS the list.
 */

export interface UserFormDialogProps {
  open: boolean;
  /** Absent = create. Present = edit that account. */
  user?: AdminUser;
  /** The catalogue, for the role picker. Empty disables role assignment. */
  roles: readonly Role[];
  /** Whether the caller may assign roles (`user.role.assign`). */
  canAssignRoles: boolean;
  onClose: () => void;
  /** A user was created or edited. The parent refreshes or merges. */
  onSaved: (user: AdminUser) => void;
}

interface FormState {
  username: string;
  fullName: string;
  email: string;
  locale: string;
  organisation: string;
  /** The dispensary's uuid, or '' for an unscoped account. */
  dispensaryUuid: string;
  canViewEscalations: boolean;
  mfaRequired: boolean;
}

type MissingField = 'username' | 'full_name' | 'email';

function stateFor(user: AdminUser | undefined): FormState {
  if (user === undefined) {
    return {
      username: '',
      fullName: '',
      email: '',
      locale: 'en',
      organisation: '',
      dispensaryUuid: '',
      canViewEscalations: false,
      mfaRequired: false,
    };
  }
  return {
    username: user.username,
    fullName: user.full_name,
    email: user.email,
    locale: user.locale,
    organisation: user.organisation ?? '',
    dispensaryUuid: user.dispensary?.uuid ?? '',
    canViewEscalations: user.can_view_escalations,
    mfaRequired: user.mfa_required,
  };
}

/*
 * THE HAND-TYPED NUMBER IS GONE, and with it the validation it needed.
 *
 * This field used to be a free-text box for the dispensary's INTEGER primary
 * key, guarded by a digits-only pattern because `Number()` accepts `"1e3"`
 * and `"0x10"` and would have silently dropped the association on a typo.
 * None of that is needed now: the value comes from a picker fed by
 * `GET /reference/dispensaries`, so it is a uuid the server already knows or
 * it is empty. An operator was never in a position to know that Aldawa is 4.
 */

function toInput(state: FormState): UserInput {
  return {
    username: state.username,
    full_name: state.fullName,
    email: state.email,
    locale: state.locale,
    organisation: state.organisation.trim() === '' ? null : state.organisation.trim(),
    dispensary: state.dispensaryUuid === '' ? null : state.dispensaryUuid,
    can_view_escalations: state.canViewEscalations,
    mfa_required: state.mfaRequired,
  };
}

const INPUT_KEYS: readonly (keyof UserInput)[] = [
  'username',
  'full_name',
  'email',
  'locale',
  'organisation',
  'dispensary',
  'can_view_escalations',
  'mfa_required',
];

/** Only the keys whose CONVERTED value differs from the value the dialog opened with. */
function diffInput(initial: UserInput, current: UserInput): Partial<UserInput> {
  const out: Record<string, unknown> = {};
  for (const key of INPUT_KEYS) {
    if (current[key] !== initial[key]) out[key] = current[key];
  }
  return out as Partial<UserInput>;
}

function sameRoles(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  const left = [...a].sort();
  const right = [...b].sort();
  return left.every((name, index) => name === right[index]);
}

function mapDetails(details: Readonly<Record<string, string[]>>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [field, messages] of Object.entries(details)) {
    const [first] = messages;
    if (first !== undefined) out[field] = first;
  }
  return out;
}

/**
 * `ApiExceptionRenderer::STATUSES` — the two codes not already covered by an
 * exported constant in `client.ts`. Same trap `UserActions.tsx` already
 * documents: neither has one yet.
 */
const FORBIDDEN_CODE = 'forbidden';

type Phase = 'form' | 'created';

export function UserFormDialog({
  open,
  user,
  roles,
  canAssignRoles,
  onClose,
  onSaved,
}: UserFormDialogProps) {
  const { t, i18n } = useTranslation();

  const isEdit = user !== undefined;

  const [state, setState] = useState<FormState>(() => stateFor(user));
  const [initialState, setInitialState] = useState<FormState>(() => stateFor(user));
  const [roleNames, setRoleNames] = useState<readonly string[]>(() => user?.roles.map((r) => r.name) ?? []);
  const [initialRoleNames, setInitialRoleNames] = useState<readonly string[]>(
    () => user?.roles.map((r) => r.name) ?? [],
  );

  const [phase, setPhase] = useState<Phase>('form');
  const [createdUser, setCreatedUser] = useState<AdminUser | null>(null);
  const [temporaryPassword, setTemporaryPassword] = useState<string | null>(null);

  const [missing, setMissing] = useState<readonly MissingField[]>([]);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [generalFailure, setGeneralFailure] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  /** See the component docblock and `MfaEnrolmentScreen.tsx`: a ref because two
   *  clicks in one React batch both read `submitting` as it was before either. */
  const inFlight = useRef(false);
  const usernameRef = useRef<HTMLInputElement>(null);

  const [dispensaries, setDispensaries] = useState<readonly Dispensary[]>([]);

  useEffect(() => {
    if (!open) return;

    let cancelled = false;

    void dispensariesApi
      .list()
      .then((rows) => {
        if (!cancelled) setDispensaries(rows);
      })
      // Deliberately swallowed: see the docblock. The field degrades to
      // "no dispensary" and every other field still saves.
      .catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, [open]);

  /*
   * THE BLANK OPTION IS FIRST AND IS A REAL CHOICE, not a placeholder. An
   * unscoped account — an administrator, a call-centre agent — is the common
   * case, and `Select`'s `placeholder` renders a DISABLED entry that cannot
   * be re-selected, which would make "remove this person's dispensary"
   * impossible once one had been set.
   *
   * A dispensary the account holds but the list does not carry (deactivated,
   * or the list failed to load) is appended, so opening the dialog can never
   * silently rewrite the field to something else on save.
   */
  const dispensaryOptions = (() => {
    const options = [
      { value: '', label: t('admin.dispensaryNone') },
      ...dispensaries.map((row) => ({
        value: row.uuid,
        label: referenceName(row, i18n.language),
      })),
    ];

    const current = user?.dispensary ?? null;

    if (current !== null && !options.some((option) => option.value === current.uuid)) {
      options.push({ value: current.uuid, label: referenceName(current, i18n.language) });
    }

    return options;
  })();

  // The dialog instance is reused across opens (create, then an edit of a
  // different row, and so on) rather than remounted, so every field this
  // form owns is reset explicitly whenever it opens, keyed on the row it is
  // about to show.
  useEffect(() => {
    if (!open) return;
    const next = stateFor(user);
    const nextRoles = user?.roles.map((r) => r.name) ?? [];
    setState(next);
    setInitialState(next);
    setRoleNames(nextRoles);
    setInitialRoleNames(nextRoles);
    setPhase('form');
    setCreatedUser(null);
    setTemporaryPassword(null);
    setMissing([]);
    setFieldErrors({});
    setGeneralFailure(null);
  }, [open, user]);

  function describeFailure(error: ApiError): string {
    switch (error.code) {
      case FORBIDDEN_CODE:
        return t('admin.notPermitted');
      case TOO_MANY_REQUESTS_CODE:
        return t('admin.throttled');
      case PAGE_EXPIRED_CODE:
        return t('auth.sessionExpired');
      case NETWORK_ERROR_CODE:
        return t('auth.unreachable');
      default:
        return t('admin.actionFailed');
    }
  }

  /** Mirrors `UserActions.tsx`'s `dismissTemporaryPassword`: the value is
   *  nulled here, immediately, not left to expire only because `open` later
   *  goes false. Captured into `saved` first — `setCreatedUser(null)` does
   *  not take effect on `createdUser` until the next render. */
  function acknowledgeTemporaryPassword(): void {
    const saved = createdUser;
    setCreatedUser(null);
    setTemporaryPassword(null);
    if (saved !== null) onSaved(saved);
    onClose();
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    // FIRST statement. Everything below can throw, and a default-action
    // submit that got through would navigate with every field's value in the
    // address bar and the referrer.
    event.preventDefault();

    if (inFlight.current) return;

    const empty: MissingField[] = [];
    if (state.username.trim() === '') empty.push('username');
    if (state.fullName.trim() === '') empty.push('full_name');
    if (state.email.trim() === '') empty.push('email');
    if (empty.length > 0) {
      setMissing(empty);
      return;
    }
    setMissing([]);

    inFlight.current = true;
    setSubmitting(true);
    setFieldErrors({});
    setGeneralFailure(null);

    try {
      if (isEdit && user !== undefined) {
        const patch = diffInput(toInput(initialState), toInput(state));

        // Guarded: `PATCH /users/{uuid}` with an EMPTY body was sent
        // unconditionally before this, including on a submit that changed
        // only roles. Three things followed from that single call: a caller
        // who holds `user.role.assign` but not `user.update` 403'd here,
        // before the roles PUT — the only permission they opened this dialog
        // for — was ever reached; an edit that changed nothing sent a
        // contentless `PATCH {}` anyway; and a role-only edit wrote a
        // contentless `user.updated` audit row (the API now early-returns on
        // that too, but the client should not send it either). `rowAfterSave`
        // falls back to `user` — the row this dialog opened with — exactly
        // the value `updated` would have carried back from a same-content
        // PATCH.
        let rowAfterSave: AdminUser = user;
        if (Object.keys(patch).length > 0) {
          rowAfterSave = await usersApi.update(user.uuid, patch);
        }

        // `rowAfterSave` is still the PATCH response (or the pre-edit row,
        // per the guard above), computed BEFORE the roles PUT below has
        // landed — it cannot know the new roles, because
        // `PATCH /users/{uuid}` does not accept them at all. Task 13c's
        // browser walk caught the row silently reverting to its PRE-edit
        // roles because this used to merge `updated` unconditionally.
        // `usersApi.assignRoles` returns the resource as the roles call
        // itself left it, so THAT is what the row is merged from whenever
        // the call ran and succeeded.
        let rolesFailed = false;
        if (canAssignRoles && roles.length > 0 && !sameRoles(initialRoleNames, roleNames)) {
          try {
            rowAfterSave = await usersApi.assignRoles(user.uuid, roleNames);
          } catch (error) {
            if (!(error instanceof ApiError)) throw error;
            setGeneralFailure(t('admin.createdRolesFailed'));
            rolesFailed = true;
          }
        }

        // NOT called when the roles call failed — see the component
        // docblock. `onSaved` is what the parent uses to close this dialog;
        // calling it here would close the dialog in the same React batch
        // that `setGeneralFailure` just painted the message into.
        if (!rolesFailed) onSaved(rowAfterSave);
      } else {
        const created = await usersApi.create(toInput(state));
        // Set immediately, unconditional on the roles call below: the panel
        // has to render (and the temporary password has to be readable) the
        // instant creation succeeds, whether or not a role was ticked at
        // all. See the component docblock — losing this to a SECOND,
        // unrelated call's failure is the one thing that must never happen.
        setCreatedUser(created.user);
        setTemporaryPassword(created.temporary_password);
        setPhase('created');

        if (canAssignRoles && roleNames.length > 0) {
          try {
            // Refines the row `onSaved` will eventually be handed
            // (`acknowledgeTemporaryPassword` reads `createdUser` fresh at
            // click time) to the roles-call response — see "Which response
            // the row is merged from" above. Deliberately NOT plumbed
            // through `setTemporaryPassword`/`setPhase`: the panel already
            // on screen keeps showing the same password and the same
            // account name (roles change neither), so this update is inert
            // until Dismiss actually reads `createdUser`.
            const afterRoles = await usersApi.assignRoles(created.user.uuid, roleNames);
            setCreatedUser(afterRoles);
          } catch (error) {
            if (!(error instanceof ApiError)) throw error;
            setGeneralFailure(t('admin.createdRolesFailed'));
          }
        }
      }
    } catch (error) {
      if (!(error instanceof ApiError)) throw error;

      if (Object.keys(error.details).length > 0) {
        // Kept open, typing intact: nothing here resets `state`.
        setFieldErrors(mapDetails(error.details));
      } else {
        setGeneralFailure(describeFailure(error));
      }
    } finally {
      inFlight.current = false;
      setSubmitting(false);
    }
  }

  const dialogTitle =
    phase === 'created'
      ? t('admin.temporaryPasswordTitle')
      : isEdit && user !== undefined
        ? t('admin.editTitle', { fullName: user.full_name })
        : t('admin.createTitle');

  return (
    <Dialog open={open} size="md" onClose={onClose} initialFocusRef={usernameRef} title={dialogTitle}>
      {/*
        Mounted from the first frame, empty. See UserActions.tsx / LoginPage.tsx
        for the same shape: assistive technology announces a change inside a
        region it is already watching.
      */}
      <div className={styles['failure']} role="alert" aria-atomic="true" data-testid="userFormFailure">
        {generalFailure}
      </div>

      {phase === 'created' && temporaryPassword !== null && createdUser !== null ? (
        <TemporaryPassword
          password={temporaryPassword}
          fullName={createdUser.full_name}
          username={createdUser.username}
          onAcknowledge={acknowledgeTemporaryPassword}
        />
      ) : (
        <form
          className={styles['form']}
          method="post"
          noValidate
          onSubmit={(event) => {
            void handleSubmit(event);
          }}
        >
          <Input
            ref={usernameRef}
            label={t('admin.fieldUsername')}
            width="fill"
            dir="ltr"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            required
            value={state.username}
            error={missing.includes('username') ? t('admin.required') : fieldErrors['username']}
            onChange={(event) => {
              const { value } = event.target;
              setState((current) => ({ ...current, username: value }));
            }}
          />

          <Input
            label={t('admin.fieldFullName')}
            width="fill"
            required
            value={state.fullName}
            error={missing.includes('full_name') ? t('admin.required') : fieldErrors['full_name']}
            onChange={(event) => {
              const { value } = event.target;
              setState((current) => ({ ...current, fullName: value }));
            }}
          />

          <Input
            label={t('admin.fieldEmail')}
            width="fill"
            dir="ltr"
            type="email"
            required
            value={state.email}
            error={missing.includes('email') ? t('admin.required') : fieldErrors['email']}
            onChange={(event) => {
              const { value } = event.target;
              setState((current) => ({ ...current, email: value }));
            }}
          />

          {/* The short fields share rows — `.form` is an auto-fill grid. */}
          <Select
            label={t('admin.fieldLocale')}
            width="fill"
            required
            options={[
              { value: 'en', label: t('admin.localeEn') },
              { value: 'ar', label: t('admin.localeAr') },
            ]}
            value={state.locale}
            error={fieldErrors['locale']}
            onChange={(event) => {
              const { value } = event.target;
              setState((current) => ({ ...current, locale: value }));
            }}
          />

          <Select
            label={t('admin.fieldDispensary')}
            width="fill"
            options={dispensaryOptions}
            /* `error` covers BOTH names: the server may answer on either
               `dispensary` (what this sends) or `dispensary_id` (what an
               older build sent), and an error nobody renders is an error
               the operator has to guess at. */
            error={fieldErrors['dispensary'] ?? fieldErrors['dispensary_id']}
            value={state.dispensaryUuid}
            onChange={(event) => {
              const { value } = event.target;
              setState((current) => ({ ...current, dispensaryUuid: value }));
            }}
          />

          <Input
            label={t('admin.fieldOrganisation')}
            width="fill"
            value={state.organisation}
            error={fieldErrors['organisation']}
            onChange={(event) => {
              const { value } = event.target;
              setState((current) => ({ ...current, organisation: value }));
            }}
          />

          <label className={styles['checkboxRow']}>
            <input
              type="checkbox"
              className={styles['checkbox']}
              checked={state.canViewEscalations}
              onChange={(event) => {
                const { checked } = event.target;
                setState((current) => ({ ...current, canViewEscalations: checked }));
              }}
            />
            {t('admin.fieldEscalations')}
          </label>

          <label className={styles['checkboxRow']}>
            <input
              type="checkbox"
              className={styles['checkbox']}
              checked={state.mfaRequired}
              onChange={(event) => {
                const { checked } = event.target;
                setState((current) => ({ ...current, mfaRequired: checked }));
              }}
            />
            {t('admin.fieldMfaRequired')}
          </label>

          {canAssignRoles && roles.length > 0 ? (
            <div className={styles['full']}>
              <RoleCheckboxes roles={roles} value={roleNames} onChange={setRoleNames} />
            </div>
          ) : null}

          <div className={styles['actions']}>
            <Button icon={<FloppyDiskIcon size={16} />} type="submit" loading={submitting}>
              {t('admin.save')}
            </Button>
            <Button icon={<XIcon size={16} />} type="button" variant="ghost" onClick={onClose}>
              {t('admin.cancel')}
            </Button>
          </div>
        </form>
      )}
    </Dialog>
  );
}
